// screen: Archive search (change add-viewstream-v3 §3.3, spec archive-search) — find the moments where something was
// said in the tenant's programmes and videos (by words and by meaning, Hebrew or English), and ask the archive: a short
// AI answer whose every sentence cites a moment, or "not found" with the closest moments. Clicking a moment plays it.
import * as React from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { Search, Sparkles } from 'lucide-react';
import { Button, EmptyState, Input, Skeleton, useToast } from '@viewstream/ui';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { can } from '~/app/permissions';
import { problemText } from '~/app/team';
import { SearchBox, searchTerm } from '~/app/SearchHint';
import { AnswerView, MomentList, MomentPlayer, useModeLabel } from '~/app/ArchiveSearch';
import { useArchiveSearch, useArchiveSettings, useAskArchive, type ArchiveAnswer, type ArchiveHit } from '~/app/archive';

interface ArchiveSearchParams { q?: string }

export const Route = createFileRoute('/_layout/t/$slug/archive')({
  component: Screen,
  validateSearch: (s: Record<string, unknown>): ArchiveSearchParams => (typeof s['q'] === 'string' && s['q'] ? { q: String(s['q']).slice(0, 200) } : {}),
});

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const toast = useToast();
  const modeLabel = useModeLabel();
  React.useEffect(() => { document.title = appTitle(t`Archive search`); }, [t]);
  const settings = useArchiveSettings(tenant.slug, can(scopes, 'ai:read' as never));
  const [q, setQ] = React.useState(search.q ?? '');
  const term = searchTerm(search.q ?? '');
  const res = useArchiveSearch(tenant.slug, settings.data?.enabled ? term : '');
  const [question, setQuestion] = React.useState('');
  const ask = useAskArchive();
  const [answer, setAnswer] = React.useState<ArchiveAnswer | null>(null);
  const [selected, setSelected] = React.useState<ArchiveHit | null>(null);
  // the search box updates the URL after a pause (shareable, back button)
  React.useEffect(() => {
    const id = setTimeout(() => {
      const next = searchTerm(q);
      if (next !== term) void navigate({ to: '/t/$slug/archive' as never, params: { slug: tenant.slug } as never, search: (next ? { q: next } : {}) as never, replace: true });
    }, 400);
    return () => clearTimeout(id);
  }, [q, term, navigate, tenant.slug]);

  const header = (
    <div>
      <h1 className="flex items-center gap-2 text-2xl font-semibold"><Search className="size-6" aria-hidden />{t`Archive search`}</h1>
      <p className="mt-1 max-w-3xl text-sm text-fg-muted">{t`Find where something was said in your programmes and videos — by words and by meaning, in Hebrew or English — or ask a question. Answers are written by AI only from the moments they cite; check them against the video before you use them.`}</p>
    </div>
  );
  if (settings.isPending) return <div className="space-y-6">{header}<Skeleton className="h-40" /></div>;
  if (settings.isError || !settings.data) return <div className="space-y-6">{header}<EmptyState title={t`Archive search is not available yet`} /></div>;
  if (!settings.data.enabled) {
    return (
      <div className="space-y-6">{header}
        <EmptyState title={t`Archive search is off`} description={t`An admin can switch it on in Settings → General → Archive search.`}
          actions={<Link to={'/t/$slug/settings' as never} params={{ slug: tenant.slug } as never} className="text-sm text-primary underline-offset-4 hover:underline">{t`Open Settings`}</Link>} />
      </div>
    );
  }
  const submitAsk = (e: React.FormEvent) => {
    e.preventDefault();
    const text = question.trim();
    if (text.length < 3) return;
    setAnswer(null);
    ask.mutate(text, {
      onSuccess: (a) => { setAnswer(a); setSelected(null); },
      onError: (err) => toast.push({ title: t`Could not answer`, description: problemText(err), tone: 'danger' }),
    });
  };
  const st = settings.data.status;
  return (
    <div className="space-y-6">
      {header}
      <p className="text-xs text-fg-muted" data-testid="archive-mode">
        {t`Indexed: ${st.indexed} programmes and videos (${st.chunks} passages), ${st.pending} waiting.`} {t`Search now: ${modeLabel(res.data?.mode ?? st.mode)}.`}
      </p>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
        <div className="space-y-6">
          <section aria-labelledby="archive-ask-h" className="space-y-2">
            <h2 id="archive-ask-h" className="flex items-center gap-2 font-semibold"><Sparkles className="size-4" aria-hidden />{t`Ask the archive`}</h2>
            <form className="flex gap-2" onSubmit={submitAsk}>
              <Input dir="auto" aria-label={t`Your question`} placeholder={settings.data.ask_placeholder || t`What did the analysts say about the shekel?`} value={question} maxLength={300}
                onChange={(e) => setQuestion(e.target.value)} data-testid="archive-question" />
              <Button type="submit" loading={ask.isPending} disabled={question.trim().length < 3}>{t`Ask`}</Button>
            </form>
            {ask.isPending ? <p className="text-sm text-fg-muted" role="status">{t`Reading the moments… (up to a minute)`}</p> : null}
            {answer ? <AnswerView a={answer} selected={selected} onSelect={setSelected} /> : null}
          </section>
          <section aria-labelledby="archive-search-h" className="space-y-2">
            <h2 id="archive-search-h" className="font-semibold">{t`Search moments`}</h2>
            <SearchBox value={q} onChange={setQ} label={t`Words or a topic`} testId="archive-search" />
            {term === '' ? null : res.isPending ? <Skeleton className="h-32" /> : res.isError ? (
              <p className="text-sm text-danger">{problemText(res.error)}</p>
            ) : res.data && res.data.moments.length ? (
              <MomentList items={res.data.moments} selected={selected} onSelect={setSelected} />
            ) : <p className="text-sm text-fg-muted">{t`No moments found.`}</p>}
          </section>
        </div>
        <div className="space-y-2 lg:sticky lg:top-4 lg:self-start">
          {selected ? (
            <>
              <h2 className="font-semibold">{selected.title || '—'}</h2>
              <MomentPlayer key={`${selected.subject_id}`} h={selected} />
            </>
          ) : <p className="text-sm text-fg-muted">{t`Pick a moment to play it.`}</p>}
        </div>
      </div>
    </div>
  );
}
